import { useEffect, useState } from "react";
import {
	Modal,
	Tabs,
	Image,
	Typography,
	Input,
	Radio,
	Button,
	Space,
	Row,
	Col,
	Collapse,
	Spin,
	Select,
} from "antd";
import { sellBoostFetch } from "../../utlifyFunction";
import { toast } from "react-toastify";

const { Title, Paragraph } = Typography;
const { Panel } = Collapse;

const { Option } = Select;

export default function DescriptionModal({
	modalVisible,
	setModalVisible,
	selectedProduct,
	newDescription,
	setNewDescription,
	fetchProducts,
}) {
	const [keywords, setKeywords] = useState("");
	const [tone, setTone] = useState("Professional");
	const [maxWords, setMaxWords] = useState(50);
	const [productType, setProductType] = useState("Other");
	const [ctaType, setCtaType] = useState("Urgency");
	const [targetAudience, setTargetAudience] = useState("");

	const [isButtonLoading, setIsButtonLoading] = useState(false);
	const [showAIInputs, setShowAIInputs] = useState(false);
	const [numberOfKeywords, setNumberOfKeywords] = useState("");
	const [keywordCountry, setKeywordCountry] = useState("");
	const [keywordTopic, setKeywordTopic] = useState("");
	const [loading, setLoading] = useState(false);

	const handleGenerate = async () => {
		setIsButtonLoading(true);
		try {
			const response = await sellBoostFetch("POST", "/AI/generate", {
				title: selectedProduct?.title || "",
				existingDescription: selectedProduct?.description || null,
				keywords,
				tone,
				maxWords,
				targetAudience,
				productType,
				ctaType,
			});
			if (response?.status === 200) {
				setNewDescription(response?.data?.description || "");
				toast.success("Description generated successfully!");
			}
		} catch (error) {
			toast.error("Failed to generate description.");
		} finally {
			setIsButtonLoading(false);
		}
	};

	const handleGenerateKeywords = async () => {
		setLoading(true);
		try {
			const response = await sellBoostFetch("POST", "/AI/getTrendingKeywords", {
				numberOfKeywords,
				country: keywordCountry,
				topicRelatedTo: keywordTopic,
			});
			if (response?.keywords) {
				setKeywords(response.keywords.join(",").replaceAll('"'));
				toast.success("Keywords generated successfully!");
			}
		} catch (error) {
			toast.error("Failed to generate keywords.");
		} finally {
			setLoading(false);
		}
	};

	const handleProductUpdate = async () => {
		try {
			const response = await sellBoostFetch("POST", "/shopify/updateProduct", {
				productId: selectedProduct?.id,
				description: newDescription,
				oldDescription: selectedProduct?.description,
			});
			if (response?.status === 200) {
				toast.success("Product description updated!");
				setModalVisible(false);
				fetchProducts();
			}
		} catch (error) {
			toast.error("Failed to update product description.");
		}
	};
	console.log(selectedProduct);

	return (
		<Modal
			title="Generate AI Description"
			visible={modalVisible}
			onCancel={() => setModalVisible(false)}
			footer={null}
			width={800}
		>
			<Spin spinning={isButtonLoading || loading}>
				<Row gutter={[16, 16]}>
					{/* Product Details */}
					<Col span={24}>
						<Image
							src={selectedProduct?.images?.edges[0]?.node?.originalSrc}
							alt={selectedProduct?.title}
							style={{ height: 150, objectFit: "cover", marginBottom: 16 }}
						/>
						<Title level={4}>{selectedProduct?.title}</Title>
						<Paragraph>Existing Description:</Paragraph>
						<Input.TextArea
							value={
								selectedProduct?.description || "No description available."
							}
							rows={3}
							disabled
						/>
					</Col>

					{/* Keywords Section */}
					<Col span={24}>
						<Paragraph>Keywords (comma-separated):</Paragraph>
						<Space direction="vertical" style={{ width: "100%" }}>
							<Input
								value={keywords}
								onChange={(e) => setKeywords(e.target.value)}
								placeholder="Enter keywords, separated by commas"
								disabled={loading}
							/>
							<Button
								type="dashed"
								onClick={() => setShowAIInputs(!showAIInputs)}
							>
								{showAIInputs ? "Cancel AI Generation" : "Generate with AI"}
							</Button>
						</Space>
						{showAIInputs && (
							<KeywordsGenerateSection
								numberOfKeywords={numberOfKeywords}
								setNumberOfKeywords={setNumberOfKeywords}
								keywordCountry={keywordCountry}
								setKeywordCountry={setKeywordCountry}
								keywordTopic={keywordTopic}
								setKeywordTopic={setKeywordTopic}
								isButtonLoading={isButtonLoading}
								handleGenerateKeywords={handleGenerateKeywords}
							/>
						)}
					</Col>

					{/* AI Configuration (Collapsible) */}
					<Col span={24}>
						<Collapse defaultActiveKey={["1"]}>
							<Panel header="AI Configuration" key="1">
								<Row gutter={[16, 16]}>
									<Col span={12}>
										<Paragraph>Tone:</Paragraph>
										<Radio.Group
											options={[
												"Professional",
												"Casual",
												"Luxury",
												"Fun",
												"Minimal",
											]}
											value={tone}
											onChange={(e) => setTone(e.target.value)}
										/>
									</Col>
									<Col span={12}>
										<Paragraph>Max Words:</Paragraph>
										<Input
											type="number"
											value={maxWords}
											onChange={(e) => setMaxWords(Number(e.target.value))}
										/>
									</Col>
									<Col span={24}>
										<Paragraph>Target Audience:</Paragraph>
										<Input
											value={targetAudience}
											onChange={(e) => setTargetAudience(e.target.value)}
											placeholder="e.g., tech enthusiasts, budget shoppers"
										/>
									</Col>
									<Col span={12}>
										<Paragraph>Product Type:</Paragraph>
										<Radio.Group
											options={[
												"Electronics",
												"Fashion",
												"Beauty",
												"Fitness",
												"Other",
											]}
											value={productType}
											onChange={(e) => setProductType(e.target.value)}
										/>
									</Col>
									<Col span={12}>
										<Paragraph>Call to Action Style:</Paragraph>
										<Radio.Group
											options={["Urgency", "Exclusivity", "Discount-based"]}
											value={ctaType}
											onChange={(e) => setCtaType(e.target.value)}
										/>
									</Col>
								</Row>
							</Panel>
						</Collapse>
					</Col>

					{/* Generated Description */}
					<Col span={24}>
						<Paragraph>Generated Description:</Paragraph>
						<Input.TextArea
							value={newDescription}
							rows={4}
							onChange={(e) => setNewDescription(e?.target?.value)}
						/>
					</Col>

					{/* Actions */}
					<Col span={24}>
						<Button
							type="primary"
							onClick={handleGenerate}
							loading={isButtonLoading}
							style={{ width: "100%", marginBottom: 8 }}
						>
							Generate AI Description
						</Button>
						<Button
							type="primary"
							onClick={handleProductUpdate}
							style={{ width: "100%" }}
						>
							Update Product Description
						</Button>
					</Col>
				</Row>
			</Spin>
		</Modal>
	);
}
const KeywordsGenerateSection = ({
	numberOfKeywords,
	setNumberOfKeywords,
	keywordCountry,
	setKeywordCountry,
	keywordTopic,
	setKeywordTopic,
	isButtonLoading,
	handleGenerateKeywords,
}) => {
	const handleSubmit = () => {
		console.log("cskdchd");
		// Validate inputs
		if (!numberOfKeywords || numberOfKeywords < 1 || numberOfKeywords > 10) {
			toast.error("Please enter a valid number of keywords (1-10).");
			return;
		}

		if (!keywordCountry) {
			toast.error("Please enter a country.");
			return;
		}

		if (!keywordTopic) {
			toast.error("Please enter a topic.");
			return;
		}

		// If all inputs are valid, call the generate function
		handleGenerateKeywords();
	};
	const [countries, setCountries] = useState([]);

	useEffect(() => {
		const fetchCountries = async () => {
			try {
				const response = await fetch("https://restcountries.com/v3.1/all");
				const data = await response.json();
				const countryList = data.map((country) => ({
					name: country.name.common,
					code: country.cca2,
					flag: country.flags?.svg,
				}));
				console.log(countryList, "chgchgchgcghfc");
				setCountries(countryList);
			} catch (error) {
				console.error("Error fetching countries:", error);
			}
		};

		fetchCountries();
	}, []);
	return (
		<Row gutter={16} align="middle" style={{ marginTop: 16 }}>
			{/* Number of Keywords */}
			<Col span={8}>
				<Input
					placeholder="Number of Keywords (1-10)"
					value={numberOfKeywords}
					onChange={(e) => setNumberOfKeywords(e.target.value)}
					type="number"
					min={1}
					max={10}
				/>
			</Col>

			{/* Keyword Country */}
			<Col span={8}>
				<Select
					placeholder="Select a country"
					value={keywordCountry}
					onChange={setKeywordCountry}
					showSearch={true}
					style={{
						width: "100%",
					}}
				>
					{countries.map(({ code, name, flag }) => (
						<Option key={code} value={code}>
							<img
								src={flag}
								alt={name}
								style={{ width: 20, height: 15, marginRight: 8 }}
							/>
							{name}
						</Option>
					))}
				</Select>
			</Col>

			{/* Keyword Topic */}
			<Col span={8}>
				<Input
					placeholder="Topic (e.g., Trading, Social Media)"
					value={keywordTopic}
					onChange={(e) => setKeywordTopic(e.target.value)}
				/>
			</Col>

			{/* Generate Button */}
			<Col span={24} style={{ marginTop: 16 }}>
				<Button
					type="primary"
					onClick={handleSubmit}
					loading={isButtonLoading}
					style={{ width: "100%" }}
				>
					Generate Keywords
				</Button>
			</Col>
		</Row>
	);
};
